import { useEventLogging } from '@AppModels/Logging';
import {
	AllEmailScanners,
	ConfigurableIntegrationType,
	EmailScannerId,
	ExternalIntegrationType,
	IntegrationProvider,
	IntegrationType,
	IUserPreferences,
	UserConfigurableIntegrationType,
} from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { Redirect, RouteComponentProps, Switch, useHistory, useRouteMatch } from 'react-router-dom';
import { toast } from 'sonner';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { isValidEmail } from '../../../../models/UiUtils';
import { useGetExternalIntegrationsQuery, useUserPreferencesMutation } from '../../../../queries';
import adwerxLogo from '../../../assets/logo_adwerx.png';
import {
	AppDrawer,
	AppDrawerToolbar,
	AppDrawerToolbarButtonAiAssistant,
	StickyAppDrawerContent,
} from '../../../components-v2/Drawer/AppDrawer';
import { Page } from '../../../components-v2/PageLayout';
import { AppBarNavigationItemPortalDestinationId } from '../../../components/AppBar';
import { EmailScannerSettings } from '../../../components/integrations/EmailScannerSettings';
import { ExternalIntegrations } from '../../../components/integrations/ExternalIntegrations';
import { IntegrationCards } from '../../../components/integrations/IntegrationCards';
import { UserConfigurableIntegration } from '../../../components/integrations/UserConfigurableIntegration';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { NavigationBreadcrumbsBar } from '../../../components/NavigationBreadcrumbsBar';
import { Portal } from '../../../components/Portal';
import { PrivateRoute } from '../../../components/PrivateRoute';
import { DefaultUniversalAssistantChat } from '../../templates/AssistantChat';
import { Ams360 } from '../Ams360';
import { Clio } from '../Clio';
import { ClioV2 } from '../ClioV2';
import { DonorPerfect } from '../DonorPerfect';
import { GoogleBusinessProfile } from '../GoogleBusinessProfile';
import { HousecallPro } from '../HousecallPro';
import { ManageAPIKeys } from '../ManageAPIKeys';
import { ManageFacebookAds } from '../ManageFacebookAds';
import { OAuthClients } from '../OAuthClients';
import { PersonalApiKeys } from '../PersonalApiKeys';
import { MicrosoftDynamics365Sales } from '../MicrosoftDynamics365Sales';
import { MyCase } from '../MyCase';
import { PracticePanther } from '../PracticePanther';
import { QQ } from '../QQ';
import { QuickBooksOnline } from '../QuickBooksOnline';
import { Redtail } from '../Redtail';
import { RedtailV2 } from '../RedtailV2';
import { Salesforce } from '../Salesforce';
import { ServiceTitan } from '../ServiceTitan';
import { WealthboxV2 } from '../WealthboxV2';
import { styleSheet } from './styles';

const breadcrumb: Record<string, string> = {
	crm: 'CRM BCC Integration',
	metaads: 'Meta Ads',
	mycase: 'MyCase',
	googlebusiness: 'Google Business',
	publicapi: 'Zapier Keys',
	personalapi: 'Personal API Keys',
	oauthclients: 'OAuth Clients',
	practicepanther: 'Practice Panther',
	microsoftdynamics365sales: 'Microsoft Dynamics 365 Sales',
	servicetitan: 'ServiceTitan',
	salesforce: 'Salesforce',
	quickbooksonline: 'QuickBooks Online',
	housecallpro: 'Housecall Pro',
};

function _Integrations() {
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const match = useRouteMatch();
	const { logPageView, logEvent } = useEventLogging('UserConfigurableIntegration');
	const history = useHistory();
	const getExternalIntegrationsQuery = useGetExternalIntegrationsQuery({
		impersonationContext: userSession.impersonationContext,
	});

	const mutation = useUserPreferencesMutation({
		onError: error => {
			logEvent('Save-Error', error);

			errorMessages.push({
				messages: [error.systemMessage],
			});
		},
		onSuccess: userPrefs => {
			userSession.user.userPreferences = userPrefs;

			toast.success('Changes were saved successfully!');
		},
	});

	const onSave = (integration: UserConfigurableIntegrationType, value: string) => {
		if (value === '' || isValidEmail(value)) {
			let preferences: IUserPreferences;
			if (integration === UserConfigurableIntegrationType.AdWerx) {
				preferences = { adwerxBccEmail: value };
			} else if (integration === UserConfigurableIntegrationType.CRM) {
				preferences = { bccEmail: value };
			}

			if (preferences) {
				logEvent('Save');
				mutation.mutate({ preferences });
			}
		} else {
			errorMessages.push({
				messages: ['Please enter a valid email address'],
			});
		}
	};
	const pathComponentNameProvider = (pathComponent: string) => {
		if (pathComponent in breadcrumb) {
			return breadcrumb[pathComponent];
		}

		switch (pathComponent.toLocaleLowerCase()) {
			case 'qqcatalyst': {
				return 'QQCatalyst';
			}
			default: {
				const emailScanner = AllEmailScanners.find(x => x.id === pathComponent);
				return (
					emailScanner?.name ||
					(pathComponent ? `${pathComponent.charAt(0).toUpperCase()}${pathComponent.substring(1)}` : pathComponent)
				);
			}
		}
	};
	const onRenderEmailScannerToggle = (emailScannerId: EmailScannerId) =>
		function renderEmailScannerToggle(props: RouteComponentProps<any>) {
			logPageView(props.location.pathname);

			return (
				<div className={css(styleSheet.emailToggleContainer)}>
					<EmailScannerSettings emailScannerId={emailScannerId} />
				</div>
			);
		};
	const onRenderConfigurableIntegration = (integration: ConfigurableIntegrationType) =>
		function renderConfigurableIntegration(props: RouteComponentProps<any>) {
			logPageView(props.location.pathname);

			switch (integration) {
				case ConfigurableIntegrationType.Redtail:
					return <Redtail />;
				case ConfigurableIntegrationType.Clio:
					return <Clio />;
				case ConfigurableIntegrationType.GoogleBusinessProfile:
					return <GoogleBusinessProfile />;
				case ConfigurableIntegrationType.QQ:
					return <QQ />;
				case ConfigurableIntegrationType.PublicAPI:
					return <ManageAPIKeys />;
				case ConfigurableIntegrationType.DonorPerfect:
					return <DonorPerfect />;
				case ConfigurableIntegrationType.Ams360:
					return (
						<React.Suspense fallback={<LoadingSpinner type='large' />}>
							<Ams360 />
						</React.Suspense>
					);
				default:
					return null;
			}
		};

	const onRenderExternalIntegration = (integration: ExternalIntegrationType) =>
		function renderExternalIntegration(props: RouteComponentProps<any>) {
			logPageView(props.location.pathname);

			switch (integration) {
				case ExternalIntegrationType.PracticePanther:
					return <PracticePanther />;
				case ExternalIntegrationType.MicrosoftDynamics365Sales:
					return <MicrosoftDynamics365Sales />;
				case ExternalIntegrationType.MyCase:
					return <MyCase />;
				case ExternalIntegrationType.Salesforce:
					return <Salesforce />;
				case ExternalIntegrationType.ServiceTitan:
					return <ServiceTitan />;
				case ExternalIntegrationType.QuickBooksOnline:
					return <QuickBooksOnline />;
				case ExternalIntegrationType.HousecallPro:
					return <HousecallPro />;
				case ExternalIntegrationType.Wealthbox:
					return <WealthboxV2 />;
				case ExternalIntegrationType.Clio:
					return <ClioV2 />;
				case ExternalIntegrationType.Redtail:
					return <RedtailV2 />;
				default:
					return null;
			}
		};

	const onRenderUserConfigurableIntegration = (integration: UserConfigurableIntegrationType) =>
		function renderUserConfigurableIntegration(props: RouteComponentProps<any>) {
			logPageView(props.location.pathname);

			switch (integration) {
				case UserConfigurableIntegrationType.AdWerx:
					return (
						<UserConfigurableIntegration
							{...props}
							inputProps={{
								defaultValue: userSession.user?.userPreferences?.adwerxBccEmail,
								id: 'adwerx-integration-email-input',
								placeholder: 'Adwerx BCC Email',
								type: 'email',
							}}
							integration={UserConfigurableIntegrationType.AdWerx}
							loading={mutation.isPending}
							logo={<img alt='Adwerx Logo' src={adwerxLogo} />}
							mainCopy='BCC an Adwerx email address with our Levitate group emails and the contacts would get added to an Adwerx campaign for ad targeting.'
							onSave={onSave}
							secondaryCopy={"This email will be BCC'd with every email you send with Levitate."}
							userSession={userSession}
						/>
					);
				case UserConfigurableIntegrationType.CRM:
					return (
						<UserConfigurableIntegration
							{...props}
							inputProps={{
								defaultValue: userSession.user?.userPreferences?.bccEmail,
								id: 'crm-integration-bcc-email-input',
								placeholder: 'BCC Email',
								type: 'email',
							}}
							integration={UserConfigurableIntegrationType.CRM}
							loading={mutation.isPending}
							logo={<div>CRM BCC Integration</div>}
							mainCopy="When you are sending messages with Levitate, you can have all your emails forwarded to your CRM. Please enter your CRM's BCC email below, and check the BCC box when sending messages."
							onSave={onSave}
							userSession={userSession}
						/>
					);
				case UserConfigurableIntegrationType.GoogleBusiness:
					return <GoogleBusinessProfile />;
				case UserConfigurableIntegrationType.MetaAds:
					return <ManageFacebookAds />;
				case UserConfigurableIntegrationType.PublicAPI:
					return <ManageAPIKeys />;
				default:
					return null;
			}
		};

	const onManageClicked = (integration: IntegrationType) => {
		if (
			Object.values(UserConfigurableIntegrationType).includes(integration as UserConfigurableIntegrationType) ||
			integration in EmailScannerId ||
			Object.values(ConfigurableIntegrationType).includes(integration as ConfigurableIntegrationType)
		) {
			history.push(`${match.path}/${integration.toLocaleLowerCase()}`);
		}
	};

	const onRenderBaseRoute = () => {
		return (
			<div className={css(styleSheet.integrationCardsContainer)}>
				<div className={css(styleSheet.integrationCardsContainerMessage)}>
					<div>Below are some integrations for the account.</div>
					<div>Admins or your account success manager can enable or disable integrations.</div>
				</div>
				<ExternalIntegrations />
				<IntegrationCards onManageClicked={onManageClicked} />
			</div>
		);
	};

	if (getExternalIntegrationsQuery.isPending) {
		return <LoadingSpinner type='large' />;
	}
	return (
		<Page>
			<Portal destination={AppBarNavigationItemPortalDestinationId}>
				<NavigationBreadcrumbsBar pathComponentNameProvider={pathComponentNameProvider} />
			</Portal>
			<Switch>
				{AllEmailScanners.map(x => {
					const key = x.id.toLocaleLowerCase();
					return (
						<PrivateRoute
							key={key}
							path={`${match.path}/${key}`}
							render={onRenderEmailScannerToggle(x.id)}
							userSession={userSession}
						/>
					);
				})}
				{Object.values(ExternalIntegrationType).map(x => {
					const hide =
						(x === ExternalIntegrationType.Clio &&
							!getExternalIntegrationsQuery.data?.some(i => i.provider === IntegrationProvider.Clio && i.enabled)) ||
						(x === ExternalIntegrationType.Redtail &&
							!getExternalIntegrationsQuery.data?.some(i => i.provider === IntegrationProvider.Redtail && i.enabled));

					return hide ? null : (
						<PrivateRoute
							key={x}
							path={`${match.path}/${x}/:tab?`}
							render={onRenderExternalIntegration(x)}
							userSession={userSession}
						/>
					);
				})}
				{Object.values(ConfigurableIntegrationType).map(x => {
					const hide =
						(x === ConfigurableIntegrationType.QQ && !userSession?.account?.integrations?.qqCatalyst?.enabled) ||
						(x === ConfigurableIntegrationType.Clio &&
							(!userSession?.account?.integrations?.clio?.enabled ||
								getExternalIntegrationsQuery.data?.some(i => i.provider === IntegrationProvider.Clio && i.enabled))) ||
						(x === ConfigurableIntegrationType.Redtail &&
							getExternalIntegrationsQuery.data?.some(i => i.provider === IntegrationProvider.Redtail && i.enabled));

					return hide ? null : (
						<PrivateRoute
							key={x}
							path={`${match.path}/${x}`}
							render={onRenderConfigurableIntegration(x)}
							userSession={userSession}
						/>
					);
				})}
				{Object.values(UserConfigurableIntegrationType).map(x => {
					return (
						<PrivateRoute
							key={x}
							path={`${match.path}/${x}`}
							render={onRenderUserConfigurableIntegration(x)}
							userSession={userSession}
						/>
					);
				})}
				<PrivateRoute
					path={`${match.path}/personalapi`}
					onLoad={(_location, session) => {
						// Personal keys are user-owned; no impersonation variant, so redirect when impersonating.
						if (session.impersonationContext?.account?.id) {
							return '/dashboard';
						}
						return session.account.features.publicApi?.enabled ? undefined : '/dashboard';
					}}
					render={() => <PersonalApiKeys />}
					userSession={userSession}
				/>
				<PrivateRoute
					path={`${match.path}/oauthclients`}
					onLoad={(_location, session) => {
						// OAuth clients are an admin-only, account-level feature with no impersonation variant.
						if (session.impersonationContext?.account?.id) {
							return '/dashboard';
						}
						if (!session.isAdmin) {
							return '/dashboard';
						}
						return session.account.features.publicApi?.enabled ? undefined : '/dashboard';
					}}
					render={() => <OAuthClients />}
					userSession={userSession}
				/>
				<PrivateRoute exact={true} path={`${match.path}`} render={onRenderBaseRoute} userSession={userSession} />
				<Redirect to={`${match.path}`} push={false} />
			</Switch>
			<AppDrawer>
				<StickyAppDrawerContent value='ai-assistant'>
					<DefaultUniversalAssistantChat />
				</StickyAppDrawerContent>
				<AppDrawerToolbar>
					<AppDrawerToolbarButtonAiAssistant value='ai-assistant' />
				</AppDrawerToolbar>
			</AppDrawer>
		</Page>
	);
}

export const Integrations = observer(_Integrations);
